import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import DateInputField from "../../../../components/commonComponents/TextInputs/DateInputs";
import { BlockOutlined, Delete } from "@mui/icons-material";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import {
    checkUserDetailsFromCookie,
    fetchBrands,
} from "../../../../api_calls/userManagement";
import SingleSelect from "../../../../components/commonComponents/Selectors/SingleSelect";
import CompanyHierarchySingleSelect from "../../../../components/commonComponents/Selectors/CompanyHierarchySingleSelect";
import { shouldShowHierarchyOwnerField } from "../../../../components/commonComponents/Selectors/companyHierarchySelectUtils";
import { standardFormatToday, clampDate } from "../../../../api_calls/utils";
import TextInputField from "../../../../components/commonComponents/TextInputs/TextInputField";
import moment from "moment-timezone";
import Confirmation from "../../../../components/commonComponents/Confirmation";
// loop_prioritization: fetchVisualLimit disabled for pre_prod deploy
// import { fetchVisualLimit, stopLoopCampaignVisuals } from "../../../../api_calls/loopCampaign";
import { fetchVisualLimit, stopLoopCampaignVisuals } from "../../../../api_calls/loopCampaign";
import { useFileStore } from "./FileProvider";
// loop_prioritization: HtmlTooltipDisabled only used by campaign type dropdown
// import { HtmlTooltip, HtmlTooltipDisabled, StyledDarkRedInformationTypography } from "../../../../components/commonComponents/StyledComponents/styled";
import { HtmlTooltip, HtmlTooltipDisabled, StyledDarkRedInformationTypography } from "../../../../components/commonComponents/StyledComponents/styled";

const GeneralInfo = ({ usedFor, fieldErrors, loopinfo, closeModal }) => {
    const currentStateData = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const { clearFiles } = useFileStore();

    const [showCampaignStopConfirmation, setShowCampaignStopConfirmation] = React.useState(false);
    const [showCampaignDeleteConfirmation, setShowCampaignDeleteConfirmation] = React.useState(false);
    // loop_prioritization: disabled for pre_prod deploy — inStore not ready
    const campaignTypeOptions = [
        { id: "commercial", label: "Commercial" },
        { id: "unlimited", label: "Unlimited" },
    ]
    // const statusBasedReadOnly =
    //     typeof currentStateData.new_loop_data !== "undefined" &&
    //         typeof currentStateData.new_loop_data.loop_start_end_status !== "undefined" &&
    //         ["Completed"].includes(currentStateData.new_loop_data.loop_start_end_status)
    //         ? true
    //         : false;

    const checkUserDetails = checkUserDetailsFromCookie();
    const userType =
      checkUserDetails.success === true ? checkUserDetails.user_detail.user_type : "";
    const hierarchyRetailers =
      typeof currentStateData.company_hierarchy !== "undefined" &&
      Array.isArray(currentStateData.company_hierarchy.retailers)
        ? currentStateData.company_hierarchy.retailers
        : [];
    const showHierarchyOwner = shouldShowHierarchyOwnerField({
      userType,
      usedFor,
      retailers: hierarchyRetailers,
    });

    const handleFieldChange = (loopData) => {
        const selectedLoopData = typeof currentStateData.new_loop_data !== "undefined" ? currentStateData.new_loop_data : {};
        dispatchAction(
            updateStateData({
                new_loop_data: { ...selectedLoopData, ...loopData },
            })
        );
    };

    // Week selection handler - sets both start and end date
    const handleWeekSelect = React.useCallback((monday, sunday, fieldRole) => {
        const today = standardFormatToday();

        const isStartDisabled =
            typeof currentStateData.new_loop_data !== "undefined" &&
            typeof currentStateData.new_loop_data.loop_start_end_status !== "undefined" &&
            ["Completed", "Active"].includes(currentStateData.new_loop_data.loop_start_end_status);

        const isEndDisabled =
            typeof currentStateData.new_loop_data !== "undefined" &&
            typeof currentStateData.new_loop_data.loop_start_end_status !== "undefined" &&
            (["Completed"].includes(currentStateData.new_loop_data.loop_start_end_status) ||
                currentStateData.new_loop_data.active === false);

        if (isStartDisabled && isEndDisabled) {
            return;
        }

        // Mirrors the start date field's minSelectable
        const startFieldMin = today;
        const clampedStart = clampDate(monday, startFieldMin, null);

        // Mirrors the end date field's minSelectable IIFE:
        // if the resolved start is today or later, end must be >= start
        const endFieldMin = clampedStart >= today ? clampedStart : today;
        const clampedEnd = clampDate(sunday, endFieldMin, null);

        if (clampedStart > clampedEnd) {
            return;
        }

        const updateData = {};

        if (fieldRole === "end") {
            // Week selected from the end date field: anchor end to Sunday, derive start from Monday
            if (!isEndDisabled) {
                updateData.loop_end_date = clampedEnd;
            }
            if (!isStartDisabled) {
                updateData.loop_start_date = clampedStart;
            }
        } else {
            // Week selected from the start date field: anchor start to Monday, derive end from Sunday
            if (!isStartDisabled) {
                updateData.loop_start_date = clampedStart;
            }
            if (!isEndDisabled) {
                updateData.loop_end_date = clampedEnd;
            }
        }

        if (Object.keys(updateData).length > 0) {
            handleFieldChange(updateData);
        }
    }, [currentStateData.new_loop_data]);

    // Global min/max for week selection styling
    const weekSelectionMin = standardFormatToday();
    const weekSelectionMax = null; // No upper limit for loop campaigns

    return (
        <Box>
            <Typography variant="body2">
                Please enter all general information of the new campaign. The earliest possible start date is today. If you choose to start the campaign today, it will begin playing in the selected stores as soon as possible — either within approximately 15 minutes or when the store opens.
            </Typography>
            <Box height={40} />
            {/* -------- campaign and owner name ------- */}
            <Stack direction={"row"} spacing={4}>
                {/* ------- campaign name -------- */}
                <TextInputField
                    fieldRequired={true}
                    elemId="campaignName"
                    label="Campaign Name"
                    fieldName="loop_name"
                    showlabel={true}
                    defaultVal={typeof currentStateData.new_loop_data !== "undefined" &&
                        typeof currentStateData.new_loop_data.loop_name !== "undefined"
                        ? currentStateData.new_loop_data.loop_name
                        : ""}
                    inputType={"text"}
                    onChangeCall={(e) => {
                        e.preventDefault();
                        handleFieldChange({
                            loop_name: e.target.value,
                        });
                    }}
                    fieldError={typeof fieldErrors.loop_name !== "undefined" && fieldErrors.loop_name !== ""
                        ? fieldErrors.loop_name
                        : ""}
                />
                {/* Hierarchy owner (cyreen/tenant create, when more than one company exists). */}
                {showHierarchyOwner === true && (
                    <CompanyHierarchySingleSelect
                        label="Owner*"
                        retailers={hierarchyRetailers}
                        value={
                            typeof currentStateData.new_loop_data !== "undefined" &&
                            typeof currentStateData.new_loop_data.loop_owner !== "undefined"
                                ? {
                                    label: currentStateData.new_loop_data.loop_owner,
                                    id: currentStateData.new_loop_data.loop_owner_id,
                                }
                                : null
                        }
                        fieldError={
                            typeof fieldErrors.loop_owner !== "undefined" && fieldErrors.loop_owner !== ""
                                ? fieldErrors.loop_owner
                                : ""
                        }
                        onChange={(selectedOption) => {
                            if (selectedOption === null) {
                                handleFieldChange({
                                    loop_owner: "",
                                    loop_owner_id: "",
                                    loop_owner_company_level: "",
                                });
                                return;
                            }
                            // Owner drives brands, visual limits, share list, and store capacity.
                            handleFieldChange({
                                loop_owner: selectedOption.label,
                                loop_owner_id: selectedOption.id,
                                loop_owner_company_level: selectedOption.company_level,
                                share_list: [],
                                share_list_selection: [],
                                loop_brand_company: "",
                                loop_brand_company_id: "",
                                loop_brand_company_name: "",
                                manual_brand_company: false,
                                loop_campaign_type: "",
                                loop_campaign_type_id: "",
                                selected_stores: [],
                            });
                            fetchBrands(selectedOption.id);
                            fetchVisualLimit(selectedOption.id);
                            clearFiles();
                        }}
                    />
                )}

            </Stack>
            <Box height={40} />
            {/* -------- Brand Company and Brand name ------- */}
            <Stack direction={"row"} spacing={4}>
                {/* -------- brand company ------- */}
                <SingleSelect
                    label="Brand Company*"
                    enableFullWidth={true}
                    elemId={"brandCompanyDropdown"}
                    options={
                        typeof currentStateData.brand_list !== "undefined" ? Object.values(currentStateData.brand_list) : []
                    }
                    selectionCheck={(option, value) => option?.id === value?.id}
                    defaultVal={
                        currentStateData.new_loop_data?.loop_brand_company
                            ? {
                                label: currentStateData.new_loop_data.loop_brand_company,
                                id: currentStateData.new_loop_data.loop_brand_company_id,
                            }
                            : ""
                    }
                    onChangeCall={(selectedOption) => {
                        if (selectedOption.id === "new") {
                            handleFieldChange({
                                manual_brand_company: true,
                                loop_brand_company: selectedOption.label,
                                loop_brand_company_id: selectedOption.id,
                            });
                            setTimeout(() => {
                                document.getElementById("brandCompanyNameField").focus();
                            }, 200);
                        } else {
                            handleFieldChange({
                                loop_brand_company: selectedOption.label,
                                loop_brand_company_id: selectedOption.id,
                                manual_brand_company: false,
                            });
                        }
                    }}
                    fieldError={
                        typeof fieldErrors.brand_company !== "undefined" && fieldErrors.brand_company !== ""
                            ? fieldErrors.brand_company
                            : ""
                    }
                />
                {/* ------------- manual brand company name ------------ */}
                {typeof currentStateData.new_loop_data !== "undefined" &&
                    typeof currentStateData.new_loop_data.manual_brand_company !== "undefined" && currentStateData.new_loop_data.manual_brand_company === true && (
                        <TextInputField
                            fieldRequired={true}
                            elemId="brandCompanyNameField"
                            label="Brand Company Name"
                            fieldName="brand_company_name"
                            defaultVal={
                                typeof currentStateData.new_loop_data !== "undefined" &&
                                    typeof currentStateData.new_loop_data.loop_brand_company_name !== "undefined"
                                    ? currentStateData.new_loop_data.loop_brand_company_name
                                    : ""
                            }
                            showlabel={true}
                            inputType={"text"}
                            onChangeCall={(e) => {
                                e.preventDefault();
                                handleFieldChange({
                                    loop_brand_company_name: e.target.value,
                                });
                            }}
                            fieldError={
                                typeof fieldErrors.loop_brand_company_name !== "undefined" && fieldErrors.loop_brand_company_name !== ""
                                    ? fieldErrors.loop_brand_company_name
                                    : ""
                            }
                        />
                    )}
                {/* ------ brand name -------- */}
                <TextInputField
                    fieldRequired={true}
                    elemId="brandName"
                    label="Brand Name"
                    fieldName="brand_name"
                    defaultVal={
                        typeof currentStateData.new_loop_data !== "undefined" &&
                            typeof currentStateData.new_loop_data.brand_name !== "undefined"
                            ? currentStateData.new_loop_data.brand_name
                            : ""
                    }
                    showlabel={true}
                    inputType={"text"}
                    onChangeCall={(e) => {
                        e.preventDefault();
                        handleFieldChange({
                            brand_name: e.target.value,
                        });
                    }}
                    fieldError={
                        typeof fieldErrors.brand_name !== "undefined" && fieldErrors.brand_name !== ""
                            ? fieldErrors.brand_name
                            : ""
                    }
                />

            </Stack>
            <Box height={40} />
            {/* ---------- start/end date ------------- */}
            <Stack direction={"row"} spacing={4}>
                {/* ------ start date -------- */}
                <DateInputField
                    fieldRequired={true}
                    elemId="startDate"
                    label="Start Date "
                    className="start_date_class"
                    fieldName="start_date"
                    minSelectable={standardFormatToday()}
                    disabled={
                        typeof currentStateData.new_loop_data !== "undefined" &&
                            typeof currentStateData.new_loop_data.loop_start_end_status !== "undefined" &&
                            ["Completed", "Active"].includes(currentStateData.new_loop_data.loop_start_end_status)
                            ? true
                            : false
                    }
                    defaultVal={
                        typeof currentStateData.new_loop_data !== "undefined" &&
                            typeof currentStateData.new_loop_data.loop_start_date !== "undefined"
                            ? currentStateData.new_loop_data.loop_start_date
                            : ""
                    }
                    showlabel={false}
                    fieldError={
                        typeof fieldErrors.loop_start_date !== "undefined" && fieldErrors.loop_start_date !== ""
                            ? fieldErrors.loop_start_date
                            : ""
                    }
                    onChangeCall={(selectedDate) => {
                        handleFieldChange({
                            loop_start_date: selectedDate,
                        });
                    }}
                    onWeekSelect={handleWeekSelect}
                    weekSelectionMinDate={weekSelectionMin}
                    weekSelectionMaxDate={weekSelectionMax}
                />
                {/* --------end date ------- */}
                <DateInputField
                    fieldRequired={true}
                    elemId="endDate"
                    label="End Date "
                    fieldName="end_date"
                    className="end_date_class"
                    minSelectable={(() => {
                        if (
                            typeof currentStateData.new_loop_data !== "undefined" &&
                            typeof currentStateData.new_loop_data.loop_start_date !== "undefined" &&
                            moment(currentStateData.new_loop_data.loop_start_date).isSameOrAfter(moment(), "day")
                        ) {
                            return currentStateData.new_loop_data.loop_start_date;
                        } else {
                            return standardFormatToday();
                        }
                    })()}
                    disabled={
                        typeof currentStateData.new_loop_data !== "undefined" &&
                            typeof currentStateData.new_loop_data.loop_start_end_status !== "undefined" &&
                            (["Completed"].includes(currentStateData.new_loop_data.loop_start_end_status) || currentStateData.new_loop_data.active === false)
                            ? true
                            : false
                    }
                    defaultVal={
                        typeof currentStateData.new_loop_data !== "undefined" &&
                            typeof currentStateData.new_loop_data.loop_end_date !== "undefined"
                            ? currentStateData.new_loop_data.loop_end_date
                            : ""
                    }
                    showlabel={false}
                    fieldError={
                        typeof fieldErrors.loop_end_date !== "undefined" && fieldErrors.loop_end_date !== ""
                            ? fieldErrors.loop_end_date
                            : ""
                    }
                    onChangeCall={(selectedDate) => {
                        handleFieldChange({
                            loop_end_date: selectedDate,
                        });
                    }}
                    onWeekSelect={handleWeekSelect}
                    weekSelectionMinDate={weekSelectionMin}
                    weekSelectionMaxDate={weekSelectionMax}
                />
            </Stack>
            <Box height={35} />
            {/* ------------- campaign type section ----------- */}

            <StyledDarkRedInformationTypography variant="body3">
                Campaign Type
            </StyledDarkRedInformationTypography>
            <Box height={10} />
            <Typography variant="body2">
                Select the campaign type for loop-based playout.
            </Typography>
            <Box height={10} />
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "max-content 1fr",
                    columnGap: "0.35em",
                }}
            >
                <Typography variant="body2"><strong>Commercial:</strong></Typography>
                <Typography variant="body2">Guarantees one playout per loop.</Typography>
                <Typography variant="body2"><strong>Unlimited:</strong></Typography>
                <Typography variant="body2">
                    No guaranteed playout per loop. May play multiple times if capacity is available. Commercial campaigns take priority.
                </Typography>
            </Box>

            <Box height={10} />


            {/* -------- Campaign type dropdown ------- */}

            <HtmlTooltipDisabled title={usedFor === "update" ? "Campaign type cannot be changed after creation" : ""} arrow>
                <Box width={"49%"}>
                    <SingleSelect
                        label="Type*"
                        enableFullWidth={true}
                        readOnlyFlag={usedFor === "update" ? true : false}
                        elemId={"loopCampaignTypeDropdown"}
                        options={campaignTypeOptions}
                        disabledOptions={(option) => currentStateData?.loop_campaign_types?.[option.id] === false ? true : false
                        }
                        disableSearch
                        tooltipTitleForDisabledOptions="Not Available"
                        selectionCheck={(option, value) => option?.id === value?.id}
                        defaultVal={
                            currentStateData.new_loop_data?.loop_campaign_type
                                ? {
                                    label: currentStateData.new_loop_data.loop_campaign_type,
                                    id: currentStateData.new_loop_data.loop_campaign_type_id,
                                }
                                : ""
                        }
                        onChangeCall={(selectedOption) => {
                            handleFieldChange({
                                loop_campaign_type_id: selectedOption.id,
                                loop_campaign_type: selectedOption.label,
                                visual_upload_limit: selectedOption.id === "commercial" ? currentStateData?.loop_campaign_types?.commercial_limit_count : currentStateData?.loop_campaign_types?.unlimited_limit_count,
                            });
                            // clear selected visuals (if any)
                            clearFiles();
                        }}
                        fieldError={
                            typeof fieldErrors.loop_campaign_type !== "undefined" && fieldErrors.loop_campaign_type !== ""
                                ? fieldErrors.loop_campaign_type
                                : ""
                        }
                    />
                </Box>

            </HtmlTooltipDisabled>


            {/* ------- Stop playout -------- */}
            {
                usedFor === "update" && loopinfo.active === true && loopinfo.status === "Active" && (
                    <>
                        <Box height={15} />
                        <ActionButton
                            label="Stop Campaign"
                            icon={<BlockOutlined />}
                            onClick={() => {
                                setShowCampaignStopConfirmation(true);
                            }}
                        />
                    </>
                )
            }
            {/* ---------- delete campaign ---------- */}
            {
                usedFor === "update" && loopinfo.active === true && loopinfo.status === "Scheduled" && (
                    <>
                        <Box height={15} />
                        <ActionButton
                            label="Delete Campaign"
                            icon={<Delete />}
                            onClick={() => {
                                setShowCampaignDeleteConfirmation(true);
                            }}
                        />
                    </>
                )
            }

            {/* --------- stop campaign confirmation --------- */}
            {
                showCampaignStopConfirmation === true && (
                    <Confirmation
                        showConfirmationPopup={showCampaignStopConfirmation}
                        closePopup={(e) => {
                            setShowCampaignStopConfirmation(false);
                        }}
                        confirmationActionCall={() => {
                            stopLoopCampaignVisuals(loopinfo.id, "", "stop_campaign")
                                .then((response) => {
                                    if (response.success === true) {
                                        setShowCampaignStopConfirmation(false);
                                        closeModal();
                                    }
                                })
                                .catch((error) => {
                                    console.error("Error stopping visual playout:", error);
                                });

                        }}
                        confirmationMessage={
                            <>
                                <Typography variant="body2">
                                    You are about to permanently stop the playout of the entire campaign.
                                </Typography>
                                <Box height={5} />
                                <Typography variant="body2">
                                    This action cannot be undone.
                                </Typography>
                                <Box height={10} />
                                <Typography variant="body2">Are you sure you want to proceed?</Typography>
                            </>
                        }
                        confirmationTitle={"Stop Campaign"}
                    />
                )
            }

            {/* ---------- delete campaign confirmation ---------- */}
            {
                showCampaignDeleteConfirmation === true && (
                    <Confirmation
                        showConfirmationPopup={showCampaignDeleteConfirmation}
                        closePopup={(e) => {
                            setShowCampaignDeleteConfirmation(false);
                        }}
                        confirmationActionCall={() => {
                            stopLoopCampaignVisuals(loopinfo.id, "", "delete_campaign")
                                .then((response) => {
                                    if (response.success === true) {
                                        setShowCampaignDeleteConfirmation(false);
                                        closeModal();
                                    }
                                })
                                .catch((error) => {
                                    console.error("Error stopping visual playout:", error);
                                });

                        }}
                        confirmationMessage={
                            <>
                                <Typography variant="body2">
                                    You are about to permanently delete this campaign.
                                </Typography>
                                <Box height={5} />
                                <Typography variant="body2">
                                    This cannot be undone.
                                </Typography>
                                <Box height={10} />
                                <Typography variant="body2">Are you sure you want to proceed?</Typography>
                            </>
                        }
                        confirmationTitle={"Delete Campaign"}
                    />
                )
            }
        </Box>
    );
};

export default GeneralInfo;
